import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Card, Grid, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExtendedAccident } from '../../../../../backendsdk';
import { TabPanel, a11yProps } from '../../../../../components/Tab';
import useCustomer from '../../../../../hooks/customer';
import useIsMobile from '../../../../../hooks/isMobile';
import { getStorageItem, setStorageItem } from '../../../../../utils/Storage';
import palette from '../../../../ColorPalette';
import AccidentCommentComponent from '../../AccidentComments';
import AccidentReminderComponent from '../../AccidentReminder';
import AccidentArtifactComponent from './AccidentArtifacts';

const IMAGE_LIST_KEY = 'OE-UseImageList';

interface DiscussionTabProps {
    details: ExtendedAccident;
    setDetails: CallableFunction;
    handleChange: CallableFunction;
    onSave: CallableFunction;
    setSending: CallableFunction;
    handlingStatusList: Array<string>;
    setHandlingStatusList: CallableFunction;
    setAlert: CallableFunction;
    updateCallbacks: Record<string, CallableFunction>;
    setUpdateCallbacks: CallableFunction;
    validateCallbacks: Record<string, CallableFunction>;
    setValidateCallbacks: CallableFunction;
    removeCallbacks: CallableFunction;
    handleSave: CallableFunction;
    sending: boolean;
}

const DiscussionTab: React.FC<DiscussionTabProps> = (props: DiscussionTabProps) => {
    const { t } = useTranslation();
    const { details } = props;
    const [newFiles, setNewFiles] = useState<Array<File> | null>(null);
    const [tab, setTab] = useState<number>(0);
    const [isImageList, setIsImageList] = useState<boolean>(getStorageItem(IMAGE_LIST_KEY) != 'false');
    const [dragAndDropHover, setDragAndDropHover] = useState<boolean>(false);
    const isMobile = useIsMobile();
    const { customerUsers } = useCustomer();

    useEffect(() => {
        setStorageItem(IMAGE_LIST_KEY, isImageList ? 'true' : 'false', 24 * 365);
    }, [isImageList]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = (target: string, value: any) => {
        props.handleChange((oldDetails: ExtendedAccident) => {
            if (oldDetails) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const newDetails: Record<string, any> = { ...oldDetails };
                newDetails[target] = value;
                return newDetails;
            }
        });
    };

    const conditionalTooltip = (component: React.ReactNode) => {
        if (details.accident_id === 0) {
            return (
                <Tooltip title={t('content.accidents.form.please_save')} followCursor>
                    <Box>{component}</Box>
                </Tooltip>
            );
        } else {
            return component;
        }
    };

    if (isMobile) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    height: '100%',
                    flexDirection: 'column',
                }}
            >
                <Tabs
                    value={tab}
                    onChange={(_, value) => setTab(value)}
                    TabIndicatorProps={{ sx: { bgcolor: 'secondary.main', height: 3 } }}
                    sx={{ width: '100%', borderBottom: `1px solid ${palette.neutral[200]}` }}
                >
                    {['files', 'comments', 'reminders'].map((tab, idx) => (
                        <Tab
                            key={tab}
                            label={<Typography fontSize={14}>{t(`content.accidents.${tab}.header`)}</Typography>}
                            {...a11yProps(idx)}
                            id={`${tab}-tab`}
                            data-testid={`${tab}-tab`}
                            sx={{ flex: 1 }}
                        />
                    ))}
                </Tabs>
                <TabPanel
                    key={`tab-panel-0`}
                    value={tab}
                    index={0}
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                    <AccidentArtifactComponent
                        details={details}
                        onSave={props.handleSave}
                        handleChange={handleChange}
                        files={newFiles}
                        setFiles={setNewFiles}
                        setAlert={props.setAlert}
                        isImageList={true}
                        setIsImageList={setIsImageList}
                        disabled={props.sending}
                    />
                </TabPanel>
                <TabPanel
                    key={`tab-panel-1`}
                    value={tab}
                    index={1}
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                    <AccidentCommentComponent
                        details={details}
                        setDetails={props.setDetails}
                        onSave={props.handleSave}
                        customerUsers={customerUsers}
                        disabled={props.sending}
                    />
                </TabPanel>
                <TabPanel
                    key={`tab-panel-2`}
                    value={tab}
                    index={2}
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                    <AccidentReminderComponent
                        accidentDetails={details}
                        users={customerUsers}
                        disabled={props.sending}
                    />
                </TabPanel>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <Grid container spacing={1} columns={2}>
                <Grid item xs={1}>
                    <Card sx={{ mb: 1 }}>
                        {conditionalTooltip(
                            <AccidentCommentComponent
                                details={details}
                                setDetails={props.setDetails}
                                onSave={props.handleSave}
                                customerUsers={customerUsers}
                                disabled={props.sending}
                            />,
                        )}
                    </Card>
                    <Card>
                        <Box>
                            {conditionalTooltip(
                                <AccidentReminderComponent
                                    accidentDetails={details}
                                    users={customerUsers}
                                    disabled={props.sending}
                                />,
                            )}
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={1}>
                    <Card
                        onDragOver={(e: React.DragEvent) => {
                            // Prevent default behavior (Prevent file from being opened)
                            e.preventDefault();
                            setDragAndDropHover(true);
                        }}
                        onDragEnter={() => {
                            setDragAndDropHover(true);
                        }}
                        onDrop={(e: React.DragEvent) => {
                            // Prevent default behavior (Prevent file from being opened)
                            e.preventDefault();
                            if (e.dataTransfer.files.length > 0) {
                                setNewFiles(Array.from(e.dataTransfer.files));
                            }
                            setDragAndDropHover(false);
                        }}
                        onDragLeave={() => {
                            setDragAndDropHover(false);
                        }}
                        onDragEnd={() => {
                            setDragAndDropHover(false);
                        }}
                        onDragExit={() => {
                            setDragAndDropHover(false);
                        }}
                        sx={{ mb: 1 }}
                    >
                        {dragAndDropHover ? (
                            <Box
                                sx={{
                                    p: 1,
                                    m: 1,
                                    height: '14rem',
                                    border: '2px dashed var(--gray-color)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <FileUploadIcon color="primary" fontSize="large" />
                                <Typography color="primary" variant="overline" display="block">
                                    {t('content.accidents.files.drop_here')}
                                </Typography>
                            </Box>
                        ) : (
                            conditionalTooltip(
                                <AccidentArtifactComponent
                                    details={details}
                                    onSave={props.handleSave}
                                    handleChange={handleChange}
                                    files={newFiles}
                                    setFiles={setNewFiles}
                                    setAlert={props.setAlert}
                                    isImageList={isImageList}
                                    setIsImageList={setIsImageList}
                                    disabled={props.sending}
                                />,
                            )
                        )}
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DiscussionTab;
